import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Form,
  message,
  Select,
  InputNumber,
  Radio,
  Space,
  Typography,
} from 'antd';
import { UserOutlined, CarOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';
import {
  createCustomerVehicle,
  VEHICLE_ASSIGNED_TO_OTHER_CODE,
} from '../services/serviceOrderService';
import { trUpperNormalize } from '../utils/trLocale';

const { Title, Text } = Typography;

const formatPhone = (phone: string | undefined): string => {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '').slice(0, 10);
  if (!digits) return phone;
  return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
    [a, b, c, d].filter(Boolean).join(' ')
  );
};

interface Account {
  id: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  phone?: string;
  email?: string;
}

const AddVehicleWithCustomer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlate = searchParams.get('plate')?.trim().toLocaleUpperCase('tr-TR') || '';

  const [customerMode, setCustomerMode] = useState<'search' | 'new'>('search');
  const [customerSearch, setCustomerSearch] = useState('');
  const [allCustomers, setAllCustomers] = useState<Account[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Account[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Account | null>(null);
  const [customerForm] = Form.useForm();
  const [vehicleForm] = Form.useForm();
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>();
  const [customerCategories, setCustomerCategories] = useState<Array<{ value: number; name: string; displayName: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    vehicleForm.setFieldsValue({ plate: initialPlate, currentKm: 0 });
  }, [initialPlate, vehicleForm]);

  useEffect(() => {
    loadAccounts();
    loadBrands();
    loadCustomerCategories();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      loadModelsForBrand(selectedBrandId);
    } else {
      setModels([]);
      vehicleForm.setFieldsValue({ modelId: undefined });
    }
  }, [selectedBrandId]);

  useEffect(() => {
    if (!customerSearch.trim()) {
      setFilteredCustomers([]);
      return;
    }
    const lower = customerSearch.toLowerCase().trim();
    const filtered = allCustomers.filter(
      (c) =>
        (c.accountName && c.accountName.toLowerCase().includes(lower)) ||
        (c.phone && c.phone.includes(customerSearch))
    );
    setFilteredCustomers(filtered.slice(0, 20));
  }, [customerSearch, allCustomers]);

  const loadAccounts = async () => {
    try {
      const { data } = await api.get('/accounts');
      const customers = (data || []).filter(
        (a: Account) => a.accountType === 'Customer' || a.accountType === 'Both'
      );
      setAllCustomers(customers);
    } catch (e) {
      console.error('Hesaplar yüklenemedi:', e);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch (e) {
      console.error('Markalar yüklenemedi:', e);
    }
  };

  const loadModelsForBrand = async (brandId: number) => {
    try {
      const data = await fetchModels(brandId);
      setModels(data);
    } catch (e) {
      console.error('Modeller yüklenemedi:', e);
    }
  };

  const loadCustomerCategories = async () => {
    try {
      const { data } = await api.get('/accounts/customer-categories');
      setCustomerCategories(data || []);
    } catch (e) {
      console.error('Müşteri kategorileri yüklenemedi:', e);
    }
  };

  const trUpper = (s: string | undefined) => (s != null && s !== '' ? String(s).toLocaleUpperCase('tr-TR') : s);

  const buildVehiclePayload = (customerId: number, reassign = false) => {
    const v = vehicleForm.getFieldsValue();
    const rawPlate = (v.plate || '').trim();
    const plate = rawPlate !== '' ? rawPlate.toLocaleUpperCase('tr-TR') : rawPlate;
    return {
      customerId,
      plate,
      vinChassis: v.vinChassis,
      brandId: v.brandId,
      modelId: v.modelId,
      make: v.make,
      model: v.model,
      year: v.year,
      color: v.color,
      engineNo: v.engineNo,
      currentKm: v.currentKm ?? 0,
      notes: v.notes,
      reassignToNewCustomer: reassign,
    };
  };

  const saveAndGo = async () => {
    try {
      await vehicleForm.validateFields();
      const plate = (vehicleForm.getFieldValue('plate') || '').trim();
      if (!plate) {
        message.warning('Plaka giriniz');
        return;
      }

      let customerId: number;

      if (customerMode === 'search' && selectedCustomer) {
        customerId = selectedCustomer.id;
      } else if (customerMode === 'new') {
        await customerForm.validateFields();
        const values = customerForm.getFieldsValue();
        const payload = {
          firstName: trUpper(values.firstName),
          lastName: trUpper(values.lastName),
          companyName: trUpper(values.companyName),
          accountType: 1,
          customerCategory: values.customerCategory,
          taxNumber: values.taxNumber,
          taxOffice: values.taxOffice,
          email: values.email,
          phone: values.phone ? String(values.phone).replace(/\s/g, '') : undefined,
          address: values.address,
        };
        const { data } = await api.post('/accounts', payload);
        customerId = data.id;
      } else {
        message.warning('Lütfen mevcut bir cari seçin veya yeni cari ekleyin');
        return;
      }

      setSaving(true);
      const created = await createCustomerVehicle(buildVehiclePayload(customerId, false));
      message.success('Araç kaydedildi');
      navigate(`/service-customers?customerId=${customerId}&vehicleId=${created.id}`);
    } catch (err: any) {
      if (err.errorFields) return;
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.code === VEHICLE_ASSIGNED_TO_OTHER_CODE) {
        const { currentCustomerName, newCustomerName } = data;
        let customerId: number;
        if (customerMode === 'search' && selectedCustomer) {
          customerId = selectedCustomer.id;
        } else if (customerMode === 'new') {
          await customerForm.validateFields();
          const values = customerForm.getFieldsValue();
          const payload = {
            firstName: trUpper(values.firstName),
            lastName: trUpper(values.lastName),
            companyName: trUpper(values.companyName),
            accountType: 1,
            customerCategory: values.customerCategory,
            taxNumber: values.taxNumber,
            taxOffice: values.taxOffice,
            email: values.email,
            phone: values.phone ? String(values.phone).replace(/\s/g, '') : undefined,
            address: values.address,
          };
          const { data: acc } = await api.post('/accounts', payload);
          customerId = acc.id;
        } else {
          setSaving(false);
          return;
        }
        const confirm = window.confirm(
          `Bu plaka başka bir müşteriye (${currentCustomerName}) kayıtlı. ${newCustomerName} adına atanacak. Devam?`
        );
        if (confirm) {
          try {
            const created = await createCustomerVehicle(buildVehiclePayload(customerId, true));
            message.success('Araç yeni müşteriye atandı');
            navigate(`/service-customers?customerId=${customerId}&vehicleId=${created.id}`);
          } catch (e2: any) {
            message.error(e2.response?.data?.message || 'Atama yapılamadı');
          }
        }
        setSaving(false);
        return;
      }
      message.error(data?.message || 'Kayıt sırasında hata oluştu');
      setSaving(false);
    }
  };

  return (
    <div>
      <Title level={2}>Yeni Araç ve Cari</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Plakayı girin, mevcut cari seçin veya yeni cari ekleyin; araç bilgilerini doldurup kaydedin.
      </Text>

      <Row gutter={24}>
        <Col span={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                Cari
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Radio.Group
              value={customerMode}
              onChange={(e) => {
                setCustomerMode(e.target.value);
                setSelectedCustomer(null);
                setCustomerSearch('');
                customerForm.resetFields();
                if (e.target.value === 'new') {
                  customerForm.setFieldsValue({ customerCategory: 1 });
                }
              }}
              style={{ marginBottom: 12 }}
            >
              <Radio value="search">Mevcut cari seç (ad/soyad ile ara)</Radio>
              <Radio value="new">Yeni cari ekle</Radio>
            </Radio.Group>

            {customerMode === 'search' && (
              <>
                <Input
                  placeholder="Ad, soyad veya telefon ile ara"
                  prefix={<SearchOutlined />}
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  allowClear
                  style={{ marginBottom: 8 }}
                />
                {selectedCustomer ? (
                  <div style={{ padding: 8, background: '#f5f5f5', borderRadius: 4, marginBottom: 8 }}>
                    <Text strong>Seçili: </Text>
                    {selectedCustomer.accountName}
                    {selectedCustomer.phone && ` · ${formatPhone(selectedCustomer.phone)}`}
                    <Button type="link" size="small" onClick={() => setSelectedCustomer(null)}>
                      Değiştir
                    </Button>
                  </div>
                ) : (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {filteredCustomers.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => {
                          setSelectedCustomer(c);
                          setCustomerSearch('');
                          setFilteredCustomers([]);
                        }}
                        style={{
                          padding: '6px 8px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        {c.accountName}
                        {c.phone && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            {formatPhone(c.phone)}
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {customerMode === 'new' && (
              <Form form={customerForm} layout="vertical">
                <Form.Item name="firstName" label="Ad" normalize={trUpperNormalize} rules={[{ required: true, message: 'Ad giriniz' }]}>
                  <Input placeholder="Ad" />
                </Form.Item>
                <Form.Item name="lastName" label="Soyad" normalize={trUpperNormalize} rules={[{ required: true, message: 'Soyad giriniz' }]}>
                  <Input placeholder="Soyad" />
                </Form.Item>
                <Form.Item name="companyName" label="Firma (opsiyonel)" normalize={trUpperNormalize}>
                  <Input placeholder="Firma adı" />
                </Form.Item>
                <Form.Item name="customerCategory" label="Müşteri kategorisi" rules={[{ required: true }]}>
                  <Select placeholder="Kategori seçin">
                    {customerCategories.map((cat) => (
                      <Select.Option key={cat.value} value={cat.value}>
                        {cat.displayName}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Telefon"
                  normalize={(val) => {
                    const digits = (val || '').replace(/\D/g, '').slice(0, 10);
                    if (!digits) return '';
                    return digits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (_: string, a: string, b: string, c: string, d: string) =>
                      [a, b, c, d].filter(Boolean).join(' ')
                    );
                  }}
                >
                  <Input placeholder="532 000 00 00" maxLength={14} />
                </Form.Item>
                <Form.Item name="email" label="E-posta">
                  <Input placeholder="E-posta" />
                </Form.Item>
                <Form.Item name="address" label="Adres">
                  <Input.TextArea rows={2} placeholder="Adres" />
                </Form.Item>
              </Form>
            )}
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <Space>
                <CarOutlined />
                Araç
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Form form={vehicleForm} layout="vertical">
              <Form.Item name="plate" label="Plaka" normalize={trUpperNormalize} rules={[{ required: true, message: 'Plaka girin' }]}>
                <Input placeholder="Örn: 34ABC123" />
              </Form.Item>
              <Form.Item name="vinChassis" label="VIN/Şasi No">
                <Input placeholder="VIN veya şasi" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="brandId" label="Marka">
                    <Select
                      placeholder="Marka"
                      showSearch
                      allowClear
                      onChange={(v) => {
                        setSelectedBrandId(v);
                        vehicleForm.setFieldsValue({ modelId: undefined });
                      }}
                      filterOption={(input, opt) =>
                        (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                      options={brands.map((b) => ({ value: b.id, label: b.name }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="modelId" label="Model">
                    <Select
                      placeholder="Model"
                      showSearch
                      allowClear
                      disabled={!selectedBrandId}
                      filterOption={(input, opt) =>
                        (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                      options={models.map((m) => ({ value: m.id, label: m.name }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="make" label="Marka (manuel)">
                    <Input placeholder="Marka" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="model" label="Model (manuel)">
                    <Input placeholder="Model" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="year" label="Yıl">
                    <InputNumber style={{ width: '100%' }} min={1900} max={new Date().getFullYear() + 1} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="color" label="Renk">
                    <Input placeholder="Renk" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="currentKm" label="Km">
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="engineNo" label="Motor No">
                <Input placeholder="Motor no" />
              </Form.Item>
              <Form.Item name="notes" label="Not">
                <Input.TextArea rows={2} placeholder="Not" />
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 16 }}>
        <Button type="primary" size="large" loading={saving} onClick={saveAndGo}>
          Kaydet ve servis müşterilerine git
        </Button>
        <Button style={{ marginLeft: 8 }} onClick={() => navigate('/dashboard')}>
          İptal
        </Button>
      </div>
    </div>
  );
};

export default AddVehicleWithCustomer;
